import { updateOEMProcurement } from "../../../services/oemProfile/oemProfile";

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Offcanvas,
  InputGroup,
  ProgressBar,
  Badge,
} from "react-bootstrap";
import { OEMProfile } from "../../../types/Profiles/oemProfile.types";
import {
  FaMicrochip,
  FaCarSide,
  FaGear,
  FaBolt,
  FaIndustry,
  FaBoxesStacked,
  FaPenToSquare,
  FaPlus,
  FaTrash,
  FaBullseye,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import { useTranslate } from "../../../hooks/LanguageProvider";


interface Props {
  profile: OEMProfile;
  onProfileUpdated?: () => void;
}

type ProcurementFormState = {
  procurementSegments: string[];
  localContentTarget: number;
};

export default function ProcurementTab({ profile, onProfileUpdated }: Props) {
  const { translate } = useTranslate();

  const [showDrawer, setShowDrawer] = useState(false);
  const [newSegment, setNewSegment] = useState("");

  const [formData, setFormData] = useState<ProcurementFormState>({
    procurementSegments: [],
    localContentTarget: 0,
  });

  useEffect(() => {
    setFormData({
      procurementSegments: profile.procurementSegments ?? [],
      localContentTarget: Number(
        String(profile.localIntegration?.localContentTarget ?? "0").replace("%", "")
      ),
    });
  }, [profile]);

  const handleOpenDrawer = () => {
    setFormData({
      procurementSegments: profile.procurementSegments ?? [],
      localContentTarget: Number(
        String(profile.localIntegration?.localContentTarget ?? "0").replace("%", "")
      ),
    });
    setNewSegment("");
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setNewSegment("");
  };

  const handleAddSegment = () => {
    const trimmed = newSegment.trim();
    if (!trimmed) return;

    const alreadyExists = formData.procurementSegments.some(
      (segment) => segment.toLowerCase() === trimmed.toLowerCase()
    );

    if (alreadyExists) {
      setNewSegment("");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      procurementSegments: [...prev.procurementSegments, trimmed],
    }));

    setNewSegment("");
  };

  const handleRemoveSegment = (indexToRemove: number) => {
    setFormData((prev) => ({
      ...prev,
      procurementSegments: prev.procurementSegments.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const handleSave = async () => {
    try {
      await updateOEMProcurement(formData);
      toast.success(translate("procurement_update_success"));
      setShowDrawer(false);
      onProfileUpdated?.();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || translate("procurement_update_error")
      );
    }
  };

  const getSegmentIcon = (segment: string) => {
    const value = segment.toLowerCase();

    if (value.includes("plastic")) {
      return {
        icon: <FaBoxesStacked />,
        bg: "#FFF4D6",
        color: "#D39E00",
      };
    }

    if (value.includes("metal")) {
      return {
        icon: <FaIndustry />,
        bg: "#EEF2F7",
        color: "#495057",
      };
    }

    if (value.includes("electronic")) {
      return {
        icon: <FaMicrochip />,
        bg: "#EAF2FF",
        color: "#0D6EFD",
      };
    }

    if (value.includes("interior")) {
      return {
        icon: <FaCarSide />,
        bg: "#F3EFFF",
        color: "#7C3AED",
      };
    }

    if (value.includes("battery")) {
      return {
        icon: <FaBolt />,
        bg: "#FFF1F0",
        color: "#DC3545",
      };
    }

    return {
      icon: <FaGear />,
      bg: "#EAF8EE",
      color: "#198754",
    };
  };

  const getTargetLevel = (value: number) => {
    if (value < 30) {
      return {
        label: translate("low"),
        bg: "#FFF1F0",
        color: "#DC3545",
      };
    }

    if (value < 70) {
      return {
        label: translate("medium"),
        bg: "#FFF4D6",
        color: "#D39E00",
      };
    }

    return {
      label: translate("high"),
      bg: "#EAF8EE",
      color: "#198754",
    };
  };

  const segments = useMemo(() => formData.procurementSegments ?? [], [formData]);
  const targetMeta = useMemo(
    () => getTargetLevel(formData.localContentTarget),
    [formData.localContentTarget]
  );

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="fw-semibold mb-1">{translate("procurement_segments")}</h4>
              <p className="text-muted mb-0">
                {translate("procurement_desc")}
              </p>
            </div>

            <Button
              variant="warning"
              className="rounded-3 px-4 fw-semibold d-inline-flex align-items-center gap-2"
              onClick={handleOpenDrawer}
            >
              <FaPenToSquare size={15} />
              {translate("edit_procurement")}
            </Button>
          </div>

          <div className="mb-4">
            <h6 className="fw-semibold mb-3">{translate("active_procurement_tags")}</h6>

            {segments.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {segments.map((segment, index) => {
                  const meta = getSegmentIcon(segment);

                  return (
                    <span
                      key={`${segment}-${index}`}
                      className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill border"
                      style={{
                        backgroundColor: meta.bg,
                        color: meta.color,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    >
                      {meta.icon}
                      <span>{segment}</span>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="border rounded-4 p-4 text-center text-muted">
                {translate("no_procurement_segments")}
              </div>
            )}
          </div>

          <div
            className="border rounded-4 p-4 mt-4"
            style={{ background: "#fffdf5" }}
          >
            <div className="d-flex align-items-start gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-4"
                style={{
                  width: 52,
                  height: 52,
                  background: "#FFF4D6",
                  color: "#D39E00",
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                <FaBullseye />
              </div>

              <div className="w-100">
                <small className="text-muted d-block mb-1">
                  {translate("local_integration_target")}
                </small>

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h4 className="fw-bold mb-0">{formData.localContentTarget}%</h4>

                  <Badge
                    pill
                    style={{
                      backgroundColor: targetMeta.bg,
                      color: targetMeta.color,
                      fontWeight: 600,
                    }}
                  >
                    {targetMeta.label}
                  </Badge>
                </div>

                <ProgressBar
                  now={formData.localContentTarget}
                  variant="warning"
                  style={{ height: "10px", borderRadius: "999px" }}
                />

                <div className="d-flex justify-content-between mt-2 text-muted small">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Offcanvas
        show={showDrawer}
        onHide={handleCloseDrawer}
        placement="end"
        backdrop
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-semibold">
            {translate("edit_procurement")}
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="mb-4">
            <p className="text-muted mb-0">
              {translate("procurement_drawer_desc")}
            </p>
          </div>

          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                {translate("local_integration_target")}
              </Form.Label>

              <Form.Range
                min={0}
                max={100}
                value={formData.localContentTarget}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    localContentTarget: Number(e.target.value),
                  }))
                }
              />

              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">0%</small>
                <strong>{formData.localContentTarget}%</strong>
                <small className="text-muted">100%</small>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                {translate("add_procurement_segment")}
              </Form.Label>

              <InputGroup>
                <Form.Control
                  type="text"
                  value={newSegment}
                  onChange={(e) => setNewSegment(e.target.value)}
                  placeholder={translate("procurement_placeholder")}
                  className="rounded-start-3"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSegment();
                    }
                  }}
                />
                <Button variant="warning" onClick={handleAddSegment}>
                  <FaPlus />
                </Button>
              </InputGroup>

              <Form.Text className="text-muted">
                {translate("add_segments_helper")}
              </Form.Text>
            </Form.Group>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-semibold mb-0">{translate("current_segments")}</h6>
                <Badge bg="light" text="dark" pill className="border">
                  {formData.procurementSegments.length}
                </Badge>
              </div>

              {formData.procurementSegments.length > 0 ? (
                <div
                  className="border rounded-4 p-3"
                  style={{
                    maxHeight: "220px",
                    overflowY: "auto",
                    background: "#fff",
                  }}
                >
                  <div className="d-flex flex-wrap gap-2">
                    {formData.procurementSegments.map((segment, index) => {
                      const meta = getSegmentIcon(segment);

                      return (
                        <span
                          key={`${segment}-${index}`}
                          className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill border"
                          style={{
                            backgroundColor: meta.bg,
                            color: meta.color,
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            maxWidth: "100%",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 14,
                            }}
                          >
                            {meta.icon}
                          </span>

                          <span
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "180px",
                            }}
                          >
                            {segment}
                          </span>

                          <button
                            type="button"
                            onClick={() => handleRemoveSegment(index)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: meta.color,
                              padding: 0,
                              marginLeft: 2,
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                            aria-label={`${translate("remove")} ${segment}`}
                            title={`${translate("remove")} ${segment}`}
                          >
                            <FaTrash size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="border rounded-4 p-3 text-muted text-center">
                  {translate("no_segments")}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-between mt-4 pt-3">
              <Button
                variant="light"
                onClick={handleCloseDrawer}
                className="rounded-3 px-4"
              >
                {translate("cancel")}
              </Button>

              <Button
                variant="warning"
                onClick={handleSave}
                className="rounded-3 px-4 fw-semibold"
              >
                {translate("save")}
              </Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}